// adminfrontend/src/admin_ui/pages/AdminVerificationsSummaryPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import adminComplianceApi from "../services/adminComplianceApi";
import "../styles/Admin.css";

function asArr(v) {
  return Array.isArray(v) ? v : [];
}

export default function AdminVerificationsSummaryPage({ adminEmail, onLogout }) {
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [groupBy, setGroupBy] = useState("day");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const normalized = useMemo(() => asArr(rows), [rows]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await adminComplianceApi.verifications({ from, to, groupBy });
      if (!res?.ok) throw new Error(res?.error || "load_failed");
      const list = res.rows ?? res.items ?? res.verifications ?? [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("[AdminVerificationsSummaryPage] load error:", e);
      setRows([]);
      setError("Unable to load verification analytics.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Compliance — Verifications</h1>
            <p className="ucx-admin-subtitle">Verification activity grouped over time.</p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}
            <div className="ucx-admin-header-actions">
              <button type="button" className="ucx-btn ucx-btn-outline" onClick={load} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button type="button" className="ucx-admin-btn ucx-admin-btn-reject" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Filters</div>
          <div className="ucx-admin-filters ucx-admin-filters-inventory">
            <label className="ucx-small">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <label className="ucx-small">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              <option value="day">Group by day</option>
              <option value="week">Group by week</option>
              <option value="month">Group by month</option>
            </select>
            <button type="button" className="ucx-btn ucx-btn-primary" onClick={load} disabled={loading}>
              Apply
            </button>
          </div>

          {error && <div className="ucx-admin-status-error">{error}</div>}
        </div>

        <div className="ucx-admin-table-wrapper">
          <table className="ucx-admin-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Result</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={3} className="ucx-admin-empty">Loading…</td></tr>
              )}

              {!loading && normalized.length === 0 && (
                <tr><td colSpan={3} className="ucx-admin-empty">No data found.</td></tr>
              )}

              {normalized.map((r, i) => (
                <tr key={`${r.period || r.day || r.date || "x"}-${i}`}>
                  <td>{r.period ?? r.day ?? r.date ?? "-"}</td>
                  <td>{r.result ?? r.status ?? "-"}</td>
                  <td>{r.count ?? r.total ?? r.n ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
